import {
  Component,
  Input,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { Subject, map, take } from 'rxjs';

import { AppState } from 'src/app/reducers';
import { selectActiveWorkspaceId } from 'src/app/core/reducers';
import { UserWorkspacesService } from 'src/app/core/services/user-workspaces.service';

import { FormsService } from 'src/app/core/services/forms.service';
import {
  UseCaseFormMapperService,
  UseCaseRepeaterField,
  UseCaseSourcePickerField,
} from 'src/app/core/services/use-case-form-mapper.service';
import { FormProperty } from 'src/app/core/models/form.models';
import {
  UseCaseInputField,
  UseCaseInputGroup,
  UseCaseWorkspaceChoice,
  UseCaseWorkspaceMode,
} from 'src/app/core/models/use-case.models';
import { TabComponent } from 'src/app/shared/utils/tab-manager';

// Fixed workspace sub-section control keys (ADR 0006, §12.1). These are wizard-
// level controls, NOT template inputs (§4) - the wizard reads them into the §9.5
// workspace object and strips them from `inputs`.
const WORKSPACE_MODE_KEY = 'workspace_mode';
const WORKSPACE_TITLE_KEY = 'workspace_title';
const WORKSPACE_ID_KEY = 'workspace_id';
const WORKSPACE_TITLE_MAX_LENGTH = 255;

/**
 * Steps 2/3 (§12.1): business inputs and preferences. One component, keyed by
 * `group` (§4.3). Maps the template's input schema (for its group) to
 * FormProperty[] via UseCaseFormMapperService and renders them through
 * `<app-dynamic-form>`; structured list fields (tag_list + itemShape) render
 * through the repeater. The wizard reloads this tab (with `fields` set) once
 * the template schema arrives, matching the feed wizard's reloadTab pattern.
 *
 * On the Preferences step (ADR 0006) a fixed Workspace sub-section renders
 * ABOVE the template-driven fields: a workspace_mode toggle whose shouldDisplay
 * reveals either a workspace_title text field (mode=new) or a workspace SELECT
 * (mode=existing, mirroring the feed-duplicate-form picker). These are ordinary
 * FormProperty controls in the same FormGroup, so they honour shouldDisplay and
 * feed combineValues() like every other field.
 */
@Component({
  selector: 'app-use-case-inputs-step',
  templateUrl: './use-case-inputs-step.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UseCaseInputsStepComponent implements OnInit, TabComponent {
  @Input() fields: UseCaseInputField[] = [];
  @Input() group: UseCaseInputGroup = 'business';
  // ADR 0006: the template's default_workspace_mode (§3.1), or 'new' when the
  // template is silent. Only consulted on the preferences step.
  @Input() workspaceMode: UseCaseWorkspaceMode = 'new';

  private readonly formsService = inject(FormsService);
  private readonly mapper = inject(UseCaseFormMapperService);
  private readonly store = inject(Store<AppState>);
  private readonly translate = inject(TranslateService);
  private readonly userWorkspaces = inject(UserWorkspacesService);

  formGroup: FormGroup = new FormGroup({});
  formGroupChange = new Subject<FormGroup>();
  formProperties: FormProperty[] = [];
  repeaterFields: UseCaseRepeaterField[] = [];
  sourcePickerFields: UseCaseSourcePickerField[] = [];

  ngOnInit(): void {
    const fields = this.fields ?? [];
    this.formProperties = this.mapper.mapFields(fields, this.group);
    this.repeaterFields = this.mapper.getRepeaterFields(fields, this.group);
    this.sourcePickerFields = this.mapper.getSourcePickerFields(
      fields,
      this.group,
    );

    // ADR 0006: the Workspace sub-section is fixed on the preferences step and
    // rendered first, above the template-driven preference fields.
    if (this.hasWorkspaceSection) {
      this.formProperties = [
        ...this.buildWorkspaceProperties(),
        ...this.formProperties,
      ];
    }

    this.formGroup = this.formsService.createFormGroup(this.formProperties);
    [...this.repeaterFields, ...this.sourcePickerFields].forEach((field) =>
      this.formGroup.addControl(
        field.key,
        new FormControl(field.defaultValue, field.validators),
      ),
    );

    if (this.hasWorkspaceSection) {
      this.seedActiveWorkspace();
    }

    this.formGroupChange.next(this.formGroup);
  }

  get hasCustomFields(): boolean {
    return this.repeaterFields.length > 0 || this.sourcePickerFields.length > 0;
  }

  /** Whether this instance renders the fixed Workspace sub-section (§12.1). */
  get hasWorkspaceSection(): boolean {
    return this.group === 'preferences';
  }

  /**
   * Read the §9.5 workspace choice from the sub-section controls. mode=new
   * carries the (trimmed, possibly empty) title; mode=existing the picked id.
   *
   * @returns the workspace choice for preview/provision
   */
  getWorkspaceChoice(): UseCaseWorkspaceChoice {
    const mode: UseCaseWorkspaceMode =
      this.formGroup.get(WORKSPACE_MODE_KEY)?.value ?? 'new';
    let outcome: UseCaseWorkspaceChoice;
    if (mode === 'existing') {
      outcome = {
        mode: 'existing',
        id: this.formGroup.get(WORKSPACE_ID_KEY)?.value ?? '',
      };
    } else {
      const title = String(
        this.formGroup.get(WORKSPACE_TITLE_KEY)?.value ?? '',
      ).trim();
      outcome = { mode: 'new', title };
    }

    return outcome;
  }

  /**
   * Prefill the new-workspace title from the preview's resolved
   * plan.workspace.title (§9.2), unless the user has already edited it. Keeps
   * the control pristine so a later preview can refresh it if inputs change.
   *
   * @param title the resolved workspace title
   * @return void
   */
  applyResolvedWorkspaceTitle(title: string): void {
    const control = this.formGroup.get(WORKSPACE_TITLE_KEY);
    if (control && control.pristine) {
      control.setValue(title, { emitEvent: false });
    }
  }

  /**
   * Build the fixed Workspace sub-section FormProperty controls (§12.1): a mode
   * toggle, plus a title field (mode=new) and a workspace picker (mode=existing)
   * gated by shouldDisplay. The picker mirrors the feed-duplicate-form pattern.
   *
   * @returns the workspace FormProperty controls
   */
  private buildWorkspaceProperties(): FormProperty[] {
    const groupKey = 'use-case-wizard.workspace.group.workspace';
    const isNew = (formGroup: FormGroup): boolean =>
      (formGroup.get(WORKSPACE_MODE_KEY)?.value ?? 'new') !== 'existing';

    const modeControl: FormProperty = {
      propertyName: WORKSPACE_MODE_KEY,
      propertyGroup: { key: groupKey },
      controlType: 'toggle-button-group',
      defaultValue: this.workspaceMode,
      formChanger: true,
      additionalConfig: {
        buttons: [
          {
            label: this.translate.instant(
              'use-case-wizard.workspace.mode.new-workspace',
            ),
            value: 'new',
            isActive: this.workspaceMode !== 'existing',
          },
          {
            label: this.translate.instant(
              'use-case-wizard.workspace.mode.existing-workspace',
            ),
            value: 'existing',
            isActive: this.workspaceMode === 'existing',
          },
        ],
      },
    };

    const titleControl: FormProperty = {
      propertyName: WORKSPACE_TITLE_KEY,
      propertyGroup: { key: groupKey },
      propertyLabel: this.translate.instant(
        'use-case-wizard.workspace.label.new-workspace-name',
      ),
      controlType: 'text',
      defaultValue: '',
      validators: [Validators.maxLength(WORKSPACE_TITLE_MAX_LENGTH)],
      additionalConfig: {
        placeholder: this.translate.instant(
          'use-case-wizard.workspace.placeholder.leave-empty-for-default-name',
        ),
      },
      shouldDisplay: (formGroup) => isNew(formGroup),
    };

    const pickerControl: FormProperty = {
      propertyName: WORKSPACE_ID_KEY,
      propertyGroup: { key: groupKey },
      propertyLabel: this.translate.instant(
        'use-case-wizard.workspace.label.target-workspace',
      ),
      controlType: 'select',
      valueOptionsAsync: this.userWorkspaces.sortedWorkspaces$().pipe(
        map((workspaces) =>
          workspaces.map((workspace) => ({
            value: workspace.id,
            name: workspace.title,
          })),
        ),
      ),
      shouldDisplay: (formGroup) => !isNew(formGroup),
      // Required only when this mode is active (§5.2 conditional requiredness).
      validateBy: (formGroup) =>
        isNew(formGroup) ? [] : [Validators.required],
    };

    return [modeControl, titleControl, pickerControl];
  }

  /**
   * Default the workspace picker to the user's active workspace, mirroring the
   * feed-duplicate-form default (§16 anchor).
   *
   * @return void
   */
  private seedActiveWorkspace(): void {
    this.store
      .select(selectActiveWorkspaceId)
      .pipe(take(1))
      .subscribe((workspaceId) =>
        this.formsService.populateFormValues(this.formGroup, {
          [WORKSPACE_ID_KEY]: workspaceId,
        }),
      );
  }
}
